import { Facets } from "@/components/Facets/Facets";
import { StandardProps } from "@/types/types";
import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import styles from "./FilterDrawer.module.scss";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { useRouteParams } from "@/hooks/useRouteParams";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import useRouteLoading from "@/hooks/useRouteLoading";
import classNames from "classnames/bind";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { extractAppliedFilters } from "@/components/AppliedFilters/utils";
import { getAnalyticsPageType } from "@/utils/setJLData";
import { LinkWithoutPrefetch } from "../LinkWithoutPrefetch/LinkWithoutPrefetch";

const cx = classNames.bind(styles);

export function FilterDrawer({
  standardProps,
  isOpen,
  handleClose,
  targetFacetName,
}: {
  standardProps: Pick<StandardProps, "productListingData" | "configProps">;
  isOpen: boolean;
  handleClose: () => void;
  targetFacetName?: string;
}) {
  const { productListingData, configProps } = standardProps;
  const { pageType, queryWithoutPageOrChunk } = useRouteParams();
  const isRouteLoading = useRouteLoading();

  const {
    productListingData: { facets },
  } = standardProps;
  if (!facets?.length) return null;

  const dispatchFacetAnalytics = () => {
    const { combination, trackingIds } = extractAppliedFilters(facets);
    dispatchLegacyAnalyticsEvent("plp.filterRemoved", {
      pagetype: getAnalyticsPageType(pageType, productListingData.results),
      filterCombination: "",
      filterCombinationNvals: "",
      appliedFilter: combination,
      appliedFilterNval: trackingIds,
    });
  };

  return (
    <Drawer
      heading="Filters"
      position="left"
      isOpen={isOpen}
      onClose={handleClose}
      className={cx("filterDrawer", {
        "filterDrawer--loading": isRouteLoading,
      })}
    >
      <Facets
        {...standardProps}
        configProps={{ ...configProps, isDrawer: true }}
        targetFacetName={targetFacetName}
      />
      <div className={styles.drawerFooter}>
        <LinkWithoutPrefetch
          className={styles.clearFilters}
          data-testid="clear-filters-button"
          href={{
            pathname: productListingData.baseFacetUrl,
            query: sanitiseQueryParams(queryWithoutPageOrChunk),
          }}
          onClick={() => dispatchFacetAnalytics()}
        >
          Clear filters
        </LinkWithoutPrefetch>
        <PrimaryButton
          size="small"
          className={styles.showButton}
          onClick={() => handleClose()}
          data-testid="filters-result-button"
        >
          View {standardProps.productListingData.results} results
        </PrimaryButton>
      </div>
    </Drawer>
  );
}
